import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Mail, Plus, ShieldCheck, Trash2, Users } from "lucide-react";

const emptyForm = { first_name: "", last_name: "", email: "", role: "coach" };

const getStoredToken = () => {
  if (typeof window === "undefined") return "";
  return (
    window.localStorage.getItem("sf_token") ||
    window.localStorage.getItem("token") ||
    window.localStorage.getItem("access_token") ||
    window.localStorage.getItem("auth_token") ||
    window.localStorage.getItem("sflearning_token") ||
    ""
  );
};

const authConfig = () => {
  const token = getStoredToken();
  return token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};
};

const fullName = (member) => {
  const directName = member?.name?.trim();
  if (directName) return directName;
  return `${member?.first_name || ""} ${member?.last_name || ""}`.trim() || member?.email || "Membre";
};

export default function AdminTeam() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [teamEndpointAvailable, setTeamEndpointAvailable] = useState(true);

  const load = async () => {
    setLoading(true);
    setMessage("");

    try {
      const r = await api.get("/admin/team", authConfig());
      setMembers(Array.isArray(r.data) ? r.data : []);
      setTeamEndpointAvailable(true);
    } catch (err) {
      const status = err?.response?.status;

      if (status === 404) {
        setTeamEndpointAvailable(false);
        setMembers([]);
        setMessage("L’endpoint équipe n’est pas encore disponible côté backend. Déploie backend/server.py du patch équipe pour activer la création coach/support/admin.");
      } else if (status === 401) {
        setMembers([]);
        setMessage("Session admin expirée ou token non envoyé. Déconnecte-toi puis reconnecte-toi en admin.");
      } else if (status === 403) {
        setMembers([]);
        setMessage("Accès refusé : ton compte n’a pas les droits admin pour gérer l’équipe.");
      } else {
        setMembers([]);
        setMessage(err?.response?.data?.detail || "Impossible de charger l’équipe pour le moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      const { data } = await api.post("/admin/team", form, authConfig());
      setForm(emptyForm);
      await load();
      setMessage(
        data.email_sent
          ? "Membre ajouté et email envoyé."
          : `Membre ajouté. SMTP non configuré : mot de passe temporaire à transmettre manuellement : ${data.temporary_password}`
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setTeamEndpointAvailable(false);
        setMessage("Création impossible : l’endpoint /admin/team n’est pas encore disponible côté backend.");
      } else if (status === 401) {
        setMessage("Session admin expirée ou token non envoyé. Déconnecte-toi puis reconnecte-toi en admin.");
      } else if (status === 403) {
        setMessage("Accès refusé : ton compte n’a pas les droits admin pour créer un membre.");
      } else {
        setMessage(err?.response?.data?.detail || "Impossible d’ajouter ce membre.");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (member) => {
    setMessage("");
    try {
      await api.patch(`/admin/team/${member.user_id}`, { active: !member.active }, authConfig());
      await load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setMessage("Modification impossible : l’endpoint équipe n’est pas encore disponible côté backend.");
      } else if (status === 401) {
        setMessage("Session admin expirée ou token non envoyé. Déconnecte-toi puis reconnecte-toi en admin.");
      } else {
        setMessage(err?.response?.data?.detail || "Impossible de modifier ce membre.");
      }
    }
  };

  const remove = async (member) => {
    const name = fullName(member);
    if (!window.confirm(`Supprimer ${name} de l’équipe ?`)) return;
    setMessage("");

    try {
      await api.delete(`/admin/team/${member.user_id}`, authConfig());
      await load();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setMessage("Suppression impossible : l’endpoint équipe n’est pas encore disponible côté backend.");
      } else if (status === 401) {
        setMessage("Session admin expirée ou token non envoyé. Déconnecte-toi puis reconnecte-toi en admin.");
      } else {
        setMessage(err?.response?.data?.detail || "Impossible de supprimer ce membre.");
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <Users className="w-8 h-8 text-brand-green" />
        <p className="label">Admin</p>
      </div>
      <h1 className="font-heading text-4xl font-black mb-2">Équipe</h1>
      <p className="text-gray-600 mb-8">Ajoute tes coachs et ton support client avec un accès personnel.</p>

      {message && <div className="mb-6 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-gray-700">{message}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        <form onSubmit={submit} className="card p-6 space-y-4 self-start">
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Plus className="w-5 h-5 text-brand-green" /> Ajouter un membre</h2>
          {!teamEndpointAvailable && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Fonction inactive tant que le backend équipe n’est pas déployé.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label block mb-1">Prénom</label><input className="input" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required /></div>
            <div><label className="label block mb-1">Nom</label><input className="input" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required /></div>
          </div>
          <div><label className="label block mb-1">Email</label><input type="email" className="input" value={form.email} onChange={(e) => update("email", e.target.value)} required /></div>
          <div>
            <label className="label block mb-1">Rôle</label>
            <select className="input" value={form.role} onChange={(e) => update("role", e.target.value)}>
              <option value="coach">Coach</option>
              <option value="support">Support client</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button type="submit" disabled={saving || !teamEndpointAvailable} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">{saving ? "Création..." : "Créer l’accès"}</button>
          <p className="text-xs text-gray-500 flex gap-2"><Mail className="w-4 h-4" /> Un email automatique est envoyé si le SMTP est configuré. Sinon le mot de passe temporaire s’affiche ici.</p>
        </form>

        <div className="card overflow-hidden">
          <div className="p-5 border-b border-emerald-100"><h2 className="font-heading text-2xl font-bold">Membres de l’équipe</h2></div>
          <div className="divide-y divide-emerald-100">
            {loading && <div className="p-8 text-center text-gray-500">Chargement de l’équipe...</div>}
            {!loading && members.map((m) => (
              <div key={m.user_id || m.email} className="p-5 flex flex-col md:flex-row md:items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-emerald-50 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-brand-green" /></div>
                <div className="flex-1">
                  <p className="font-semibold">{fullName(m)}</p>
                  <p className="text-sm text-gray-500">{m.email}</p>
                </div>
                <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-brand-green">{m.role}</span>
                <button type="button" onClick={() => toggleActive(m)} className="btn-ghost !px-3 !py-2 text-sm">{m.active === false ? "Réactiver" : "Désactiver"}</button>
                <button type="button" onClick={() => remove(m)} className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {!loading && members.length === 0 && <div className="p-8 text-center text-gray-500">Aucun membre pour le moment.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
